import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Injectable, inject } from '@angular/core';
import { GetSavedSearches, GetSearchHistory, PostSearch, DeleteSearch, PostSavedSearchToHistory } from './searches.actions';
import { Observable, tap } from 'rxjs';
import { append, patch, removeItem } from '@ngxs/store/operators';
import { SearchesService } from './searches.service';
import { Search } from './models/search';
import { LoadingModel } from '../../../_core/state/loading.model';

export type SearchesStateModel = LoadingModel & {
  searchHistory: Search[];
  savedSearches: Search[];
};

export const initialState: SearchesStateModel = {
  isLoading: false,
  searchHistory: [],
  savedSearches: [],
};

@State<SearchesStateModel>({
  name: 'searches',
  defaults: initialState,
})
@Injectable()
export class SearchesState {
  private readonly searchesService = inject(SearchesService);

  @Selector()
  static getSearchHistory(state: SearchesStateModel) {
    return state.searchHistory;
  }

  @Selector()
  static getSavedSearches(state: SearchesStateModel) {
    return state.savedSearches;
  }

  @Selector()
  static getIsLoading(state: SearchesStateModel) {
    return state.isLoading;
  }

  @Action(GetSearchHistory)
  getSearchHistory(context: StateContext<SearchesStateModel>): Observable<Search[]> {
    context.setState(patch<SearchesStateModel>({ isLoading: true }));
    return this.searchesService.getSearchHistory().pipe(
      tap((searchHistory) => {
        context.setState(patch<SearchesStateModel>({ searchHistory: searchHistory, isLoading: false }));
      })
    );
  }

  @Action(GetSavedSearches)
  getSearches(context: StateContext<SearchesStateModel>): Observable<Search[]> {
    context.setState(patch<SearchesStateModel>({ isLoading: true }));
    return this.searchesService.getSearches().pipe(
      tap((savedSearches) => {
        context.setState(patch<SearchesStateModel>({ savedSearches: savedSearches, isLoading: false }));
      })
    );
  }

  @Action(PostSearch)
  postSearch(context: StateContext<SearchesStateModel>, { search }: PostSearch) {
    return this.searchesService.postSearch(search).pipe(
      tap((response) => {
        context.setState(
          patch<SearchesStateModel>({
            savedSearches: append<Search>([response]),
          })
        );
      })
    );
  }

  @Action(DeleteSearch)
  deleteSearch(context: StateContext<SearchesStateModel>, { searchId }: DeleteSearch) {
    return this.searchesService.deleteSearch(searchId).pipe(
      tap(() => {
        context.setState(
          patch<SearchesStateModel>({
            savedSearches: removeItem<Search>((search) => search.id === searchId),
          })
        );
      })
    );
  }

  @Action(PostSavedSearchToHistory)
  postSavedSearchToHistory(context: StateContext<SearchesStateModel>, { search }: PostSavedSearchToHistory) {
    return this.searchesService.postSavedSearchToHistory(search).pipe(
      tap((response) => {
        context.setState(
          patch<SearchesStateModel>({
            searchHistory: append<Search>([response]),
          })
        );
      })
    );
  }
}
